import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Loader2, Globe, ImageIcon, ScanSearch, Plus, Trash2 } from 'lucide-react';
import { Input, Select, Checkbox, CheckboxGroup, Button, fieldStyles } from '../ui';
import { PCCase, FieldConfidence, FormFactor, ReferralLink } from '../../types';
import { fetchManufacturers } from '../../services/caseService';

interface CaseFormEditorProps {
  data: Partial<PCCase>;
  confidenceScores: FieldConfidence[];
  errors: Record<string, string>;
  onChange: (field: string, value: unknown) => void;
  onExtract?: (url: string) => Promise<void>;
  isExtracting?: boolean;
  onFetchImages?: () => Promise<void>;
  isFetchingImages?: boolean;
  showImageSection?: boolean;
  showReferralLinks?: boolean;
  updatedFields?: Set<string>;
}

const SIZE_OPTIONS = [
  { value: 'Full-Tower', label: 'Full-Tower' },
  { value: 'Mid-Tower', label: 'Mid-Tower' },
  { value: 'Mini-Tower', label: 'Mini-Tower' },
  { value: 'SFF', label: 'SFF' },
  { value: 'HTPC', label: 'HTPC' },
];

const SIDE_PANEL_OPTIONS = [
  { value: 'Tempered Glass', label: 'Tempered Glass' },
  { value: 'Acrylic', label: 'Acrylic' },
  { value: 'Steel', label: 'Steel' },
  { value: 'Mesh', label: 'Mesh' },
];

const FORM_FACTORS: FormFactor[] = ['E-ATX', 'ATX', 'Micro-ATX', 'Mini-ITX', 'Mini-DTX'];

export const CaseFormEditor: React.FC<CaseFormEditorProps> = ({
  data,
  confidenceScores,
  errors,
  onChange,
  onExtract,
  isExtracting,
  onFetchImages,
  isFetchingImages,
  showImageSection = true,
  showReferralLinks = true,
  updatedFields,
}) => {
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [extractUrl, setExtractUrl] = useState(data.productPageUrl || '');
  const [colorsText, setColorsText] = useState(data.colors?.join(', ') || '');

  useEffect(() => {
    fetchManufacturers().then(setManufacturers).catch(console.error);
  }, []);

  // Resync raw colors text when colors change externally (e.g. AI extract),
  // without clobbering a trailing comma while the user is typing
  useEffect(() => {
    const incoming = data.colors || [];
    if (parseColors(colorsText).join('|') !== incoming.join('|')) {
      setColorsText(incoming.join(', '));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.colors]);

  const getConfidence = (field: string): 'high' | 'medium' | 'low' | undefined => {
    const score = confidenceScores.find((s) => s.field === field);
    return score?.confidence;
  };

  const getConfidenceClass = (field: string): string => {
    if (updatedFields?.has(field)) return 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-surface rounded-lg';
    const confidence = getConfidence(field);
    if (confidence === 'low') return 'ring-2 ring-amber-300 ring-offset-2 ring-offset-surface rounded-lg';
    if (confidence === 'medium') return 'ring-1 ring-amber-200 ring-offset-2 ring-offset-surface rounded-lg';
    return '';
  };

  const handleFormFactorToggle = (ff: FormFactor) => {
    const current = data.formFactorSupport || [];
    const updated = current.includes(ff)
      ? current.filter((f) => f !== ff)
      : [...current, ff];
    onChange('formFactorSupport', updated);
  };

  const handleExtract = () => {
    if (onExtract && extractUrl.trim()) {
      onExtract(extractUrl.trim());
    }
  };

  return (
    <div className="space-y-8">
      {/* Product Page URL Section */}
      {onExtract && (
        <FormSection title="Product Page">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="https://example.com/product-page"
                value={extractUrl}
                onChange={(e) => {
                  setExtractUrl(e.target.value);
                  onChange('productPageUrl', e.target.value);
                }}
              />
            </div>
            <Button
              onClick={handleExtract}
              disabled={isExtracting || !extractUrl.trim()}
              className="shrink-0"
            >
              {isExtracting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Globe size={16} />
                  Extract
                </>
              )}
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted">
            Paste a product page URL and click Extract to auto-fill specifications using AI.
          </p>
        </FormSection>
      )}

      {/* Case Image Section */}
      {showImageSection && <FormSection title="Case Image">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Input
              label="Image URL"
              placeholder="https://example.com/case-image.jpg"
              value={data.imageUrl || ''}
              onChange={(e) => onChange('imageUrl', e.target.value)}
            />
          </div>
          {onFetchImages && !data.imageUrl && (
            <Button
              variant="secondary"
              onClick={onFetchImages}
              disabled={!data.productPageUrl || isFetchingImages}
              title={!data.productPageUrl ? 'Set Product Page URL first' : 'Fetch images from product page'}
              className="shrink-0"
            >
              {isFetchingImages ? (
                <><Loader2 size={16} className="animate-spin" /> Fetching...</>
              ) : (
                <><ScanSearch size={16} /> Fetch Images</>
              )}
            </Button>
          )}
        </div>
        {data.imageUrl && (
          <div className="mt-3 flex items-start gap-3">
            <img
              src={data.imageUrl}
              alt="Case preview"
              className="w-24 h-24 object-contain rounded-lg border border-line bg-surface-2"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden flex items-center gap-2 text-sm text-muted">
              <ImageIcon size={16} />
              <span>Image failed to load</span>
            </div>
          </div>
        )}
        <p className="mt-2 text-xs text-muted">
          Direct link to a product image. Auto-filled during extraction.
        </p>
      </FormSection>}

      {/* Basic Info Section */}
      <FormSection title="Basic Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={getConfidenceClass('name')}>
            <Input
              label="Case Name *"
              placeholder="e.g., H7 Flow"
              value={data.name || ''}
              onChange={(e) => onChange('name', e.target.value)}
              error={errors.name}
            />
            <ConfidenceIndicator confidence={getConfidence('name')} />
          </div>

          <div className={getConfidenceClass('manufacturer')}>
            <ManufacturerInput
              label="Manufacturer *"
              value={data.manufacturer || ''}
              manufacturers={manufacturers}
              onChange={(value) => onChange('manufacturer', value)}
              error={errors.manufacturer}
            />
            <ConfidenceIndicator confidence={getConfidence('manufacturer')} />
          </div>

          <div className={getConfidenceClass('sizeCategory')}>
            <Select
              label="Size Category *"
              placeholder="Select size..."
              options={SIZE_OPTIONS}
              value={data.sizeCategory || ''}
              onChange={(value) => onChange('sizeCategory', value)}
              error={errors.sizeCategory}
            />
            <ConfidenceIndicator confidence={getConfidence('sizeCategory')} />
          </div>

          <div className={getConfidenceClass('sidePanel')}>
            <label className="block text-sm font-medium text-fg mb-1">Side Panel</label>
            <CheckboxGroup
              options={SIDE_PANEL_OPTIONS}
              selected={data.sidePanel || []}
              onChange={(value) => onChange('sidePanel', value)}
            />
            <ConfidenceIndicator confidence={getConfidence('sidePanel')} />
          </div>
        </div>
      </FormSection>

      {/* Dimensions Section */}
      <FormSection title="Dimensions (mm)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={getConfidenceClass('dimensions.height')}>
            <Input
              label="Height *"
              type="number"
              placeholder="e.g., 480"
              value={data.dimensions?.height || ''}
              onChange={(e) =>
                onChange('dimensions.height', parseFloat(e.target.value) || 0)
              }
              error={errors['dimensions.height']}
            />
            <ConfidenceIndicator confidence={getConfidence('dimensions.height')} />
          </div>

          <div className={getConfidenceClass('dimensions.width')}>
            <Input
              label="Width *"
              type="number"
              placeholder="e.g., 230"
              value={data.dimensions?.width || ''}
              onChange={(e) =>
                onChange('dimensions.width', parseFloat(e.target.value) || 0)
              }
              error={errors['dimensions.width']}
            />
            <ConfidenceIndicator confidence={getConfidence('dimensions.width')} />
          </div>

          <div className={getConfidenceClass('dimensions.depth')}>
            <Input
              label="Depth *"
              type="number"
              placeholder="e.g., 505"
              value={data.dimensions?.depth || ''}
              onChange={(e) =>
                onChange('dimensions.depth', parseFloat(e.target.value) || 0)
              }
              error={errors['dimensions.depth']}
            />
            <ConfidenceIndicator confidence={getConfidence('dimensions.depth')} />
          </div>
        </div>

        <div className="mt-4">
          <Input
            label="Weight (kg)"
            type="number"
            step="0.1"
            placeholder="e.g., 9.5"
            value={data.weight || ''}
            onChange={(e) => onChange('weight', parseFloat(e.target.value) || 0)}
          />
        </div>
      </FormSection>

      {/* Clearances Section */}
      <FormSection title="Component Clearances (mm)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={getConfidenceClass('maxGpuLength')}>
            <Input
              label="Max GPU Length *"
              type="number"
              placeholder="e.g., 400"
              value={data.maxGpuLength || ''}
              onChange={(e) =>
                onChange('maxGpuLength', parseFloat(e.target.value) || 0)
              }
              error={errors.maxGpuLength}
            />
            <ConfidenceIndicator confidence={getConfidence('maxGpuLength')} />
          </div>

          <div className={getConfidenceClass('maxCpuCoolerHeight')}>
            <Input
              label="Max CPU Cooler Height *"
              type="number"
              placeholder="e.g., 185"
              value={data.maxCpuCoolerHeight || ''}
              onChange={(e) =>
                onChange('maxCpuCoolerHeight', parseFloat(e.target.value) || 0)
              }
              error={errors.maxCpuCoolerHeight}
            />
            <ConfidenceIndicator confidence={getConfidence('maxCpuCoolerHeight')} />
          </div>

          <div className={getConfidenceClass('maxPsuLength')}>
            <Input
              label="Max PSU Length"
              type="number"
              placeholder="e.g., 200"
              value={data.maxPsuLength || ''}
              onChange={(e) =>
                onChange('maxPsuLength', parseFloat(e.target.value) || 0)
              }
              error={errors.maxPsuLength}
            />
            <ConfidenceIndicator confidence={getConfidence('maxPsuLength')} />
          </div>
        </div>
      </FormSection>

      {/* Form Factors Section */}
      <FormSection title="Motherboard Support">
        <div className="flex flex-wrap gap-3">
          {FORM_FACTORS.map((ff) => (
            <label
              key={ff}
              className={`
                flex items-center gap-2 h-10 px-3 rounded-lg border cursor-pointer
                transition-colors duration-150
                ${
                  data.formFactorSupport?.includes(ff)
                    ? 'border-accent bg-accent-soft text-accent-ink'
                    : 'border-line-strong hover:border-fg text-fg'
                }
              `}
            >
              <Checkbox
                checked={data.formFactorSupport?.includes(ff) || false}
                onChange={() => handleFormFactorToggle(ff)}
              />
              <span className="text-sm font-medium">{ff}</span>
            </label>
          ))}
        </div>
        {errors.formFactorSupport && (
          <p className="mt-2 text-sm text-danger">{errors.formFactorSupport}</p>
        )}
      </FormSection>

      {/* Drive Bays Section */}
      <FormSection title="Drive Bays">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label='3.5" Internal'
            type="number"
            min="0"
            placeholder="0"
            value={data.driveBays?.internal35 || ''}
            onChange={(e) =>
              onChange('driveBays', {
                ...data.driveBays,
                internal35: parseInt(e.target.value) || 0,
              })
            }
          />

          <Input
            label='2.5" Internal'
            type="number"
            min="0"
            placeholder="0"
            value={data.driveBays?.internal25 || ''}
            onChange={(e) =>
              onChange('driveBays', {
                ...data.driveBays,
                internal25: parseInt(e.target.value) || 0,
              })
            }
          />

          <Input
            label='5.25" External'
            type="number"
            min="0"
            placeholder="0"
            value={data.driveBays?.external525 || ''}
            onChange={(e) =>
              onChange('driveBays', {
                ...data.driveBays,
                external525: parseInt(e.target.value) || 0,
              })
            }
          />
        </div>
      </FormSection>

      {/* I/O Panel Section */}
      <FormSection title="Front I/O Panel">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Input
            label="USB 3.0"
            type="number"
            min="0"
            placeholder="0"
            value={data.ioPanel?.usb3 || ''}
            onChange={(e) =>
              onChange('ioPanel', {
                ...data.ioPanel,
                usb3: parseInt(e.target.value) || 0,
              })
            }
          />

          <Input
            label="USB 2.0"
            type="number"
            min="0"
            placeholder="0"
            value={data.ioPanel?.usb2 || ''}
            onChange={(e) =>
              onChange('ioPanel', {
                ...data.ioPanel,
                usb2: parseInt(e.target.value) || 0,
              })
            }
          />

          <Input
            label="USB-C"
            type="number"
            min="0"
            placeholder="0"
            value={data.ioPanel?.usbC || ''}
            onChange={(e) =>
              onChange('ioPanel', {
                ...data.ioPanel,
                usbC: parseInt(e.target.value) || 0,
              })
            }
          />

          <div className="flex items-center gap-2 pt-6">
            <Checkbox
              checked={data.ioPanel?.audio || false}
              onChange={(checked) =>
                onChange('ioPanel', { ...data.ioPanel, audio: checked })
              }
            />
            <span className="text-sm text-fg">Audio Jack</span>
          </div>

          <div className="flex items-center gap-2 pt-6">
            <Checkbox
              checked={data.ioPanel?.micIn || false}
              onChange={(checked) =>
                onChange('ioPanel', { ...data.ioPanel, micIn: checked })
              }
            />
            <span className="text-sm text-fg">Mic Input</span>
          </div>
        </div>
      </FormSection>

      {/* Referral Links Section */}
      {showReferralLinks && <FormSection title="Referral Links">
        <div className="space-y-3">
          {(data.referralLinks || []).map((link, index) => (
            <div key={index} className="flex gap-2 items-end">
              <div className="flex-1">
                <Input
                  label={index === 0 ? 'Retailer' : undefined}
                  placeholder="e.g., Amazon"
                  value={link.retailer || ''}
                  onChange={(e) => {
                    const updated = [...(data.referralLinks || [])];
                    updated[index] = { ...updated[index], retailer: e.target.value };
                    onChange('referralLinks', updated);
                  }}
                />
              </div>
              <div className="flex-[2]">
                <Input
                  label={index === 0 ? 'URL' : undefined}
                  placeholder="https://amazon.com/dp/..."
                  value={link.url || ''}
                  onChange={(e) => {
                    const updated = [...(data.referralLinks || [])];
                    updated[index] = { ...updated[index], url: e.target.value };
                    onChange('referralLinks', updated);
                  }}
                />
              </div>
              <div className="w-28">
                <Input
                  label={index === 0 ? 'Price ($)' : undefined}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="89.99"
                  value={link.price || ''}
                  onChange={(e) => {
                    const updated = [...(data.referralLinks || [])];
                    updated[index] = { ...updated[index], price: parseFloat(e.target.value) || 0 };
                    onChange('referralLinks', updated);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const updated = (data.referralLinks || []).filter((_, i) => i !== index);
                  onChange('referralLinks', updated);
                }}
                className="inline-flex h-10 w-10 items-center justify-center text-muted hover:text-danger hover:bg-danger-soft rounded-lg transition-colors"
                title="Remove link"
                aria-label="Remove link"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <Button
            variant="secondary"
            onClick={() => {
              const updated = [...(data.referralLinks || []), { retailer: '', url: '', price: 0 } as ReferralLink];
              onChange('referralLinks', updated);
            }}
            className="mt-2"
          >
            <Plus size={16} />
            Add Link
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted">
          Add affiliate or referral links with prices for this case.
        </p>
      </FormSection>}

      {/* Additional Info Section */}
      <FormSection title="Additional Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={getConfidenceClass('releaseYear')}>
            <Input
              label="Release Year"
              type="number"
              min="2010"
              max={new Date().getFullYear()}
              placeholder="e.g., 2023"
              value={data.releaseYear || ''}
              onChange={(e) =>
                onChange('releaseYear', parseInt(e.target.value) || 0)
              }
              error={errors.releaseYear}
            />
            <ConfidenceIndicator confidence={getConfidence('releaseYear')} />
          </div>
        </div>

        <div className="mt-4">
          <Input
            label="Colors (comma-separated)"
            placeholder="e.g., Black, White, Black/Red"
            value={colorsText}
            onChange={(e) => {
              setColorsText(e.target.value);
              onChange('colors', parseColors(e.target.value));
            }}
          />
        </div>
      </FormSection>
    </div>
  );
};

// Helpers
const parseColors = (text: string): string[] =>
  text.split(',').map((c) => c.trim()).filter(Boolean);

// Helper Components
const FormSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div>
    <h3 className="mb-4 border-b border-line pb-2 font-mono text-xs uppercase tracking-label text-muted">
      {title}
    </h3>
    {children}
  </div>
);

const ConfidenceIndicator: React.FC<{
  confidence?: 'high' | 'medium' | 'low';
}> = ({ confidence }) => {
  if (!confidence || confidence === 'high') return null;

  return (
    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
      {confidence === 'low' ? 'Low confidence - please verify' : 'Medium confidence'}
    </p>
  );
};

// Manufacturer input with autocomplete for existing manufacturers
const ManufacturerInput: React.FC<{
  label: string;
  value: string;
  manufacturers: string[];
  onChange: (value: string) => void;
  error?: string;
}> = ({ label, value, manufacturers, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredManufacturers = manufacturers.filter((m) =>
    m.toLowerCase().includes(inputValue.toLowerCase())
  );

  const isNewManufacturer =
    inputValue.trim() !== '' &&
    !manufacturers.some((m) => m.toLowerCase() === inputValue.toLowerCase());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleSelect = (manufacturer: string) => {
    setInputValue(manufacturer);
    onChange(manufacturer);
    setIsOpen(false);
  };

  return (
    <div className="w-full relative">
      <label className="block text-sm font-medium text-fg mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`
            ${fieldStyles} h-10 pl-3 pr-10
            ${error ? 'border-danger' : ''}
          `}
          placeholder="Select or enter manufacturer..."
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDown size={18} />
        </button>
      </div>

      {isOpen && (filteredManufacturers.length > 0 || isNewManufacturer) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-surface border border-line rounded-lg shadow-pop max-h-60 overflow-auto"
        >
          {isNewManufacturer && (
            <button
              type="button"
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-2 text-accent-ink font-medium border-b border-line"
              onClick={() => handleSelect(inputValue.trim())}
            >
              + Add new manufacturer: "{inputValue.trim()}"
            </button>
          )}
          {filteredManufacturers.map((m) => (
            <button
              key={m}
              type="button"
              className={`
                w-full px-4 py-2.5 text-left text-sm hover:bg-surface-2
                ${m === value ? 'bg-accent-soft text-accent-ink' : 'text-fg'}
              `}
              onClick={() => handleSelect(m)}
            >
              {m}
            </button>
          ))}
        </div>
      )}

      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
};
